import { apiClient } from '@/api/client';
import { cleanParams } from '@/api/queryParams';
import type {
  ExportHistoryItem,
  ExportFormat,
  ExportJobCreateResponse,
  ExportJobStatus,
  ExportRequest,
  Paginated,
} from '@/types';

export async function downloadSavedInspections(
  format: ExportFormat,
  filters: { date_from?: string; date_to?: string },
): Promise<Blob> {
  const response = await apiClient.get<Blob>(`/scan-exports/inspections.${format}`, {
    params: cleanParams(filters),
    responseType: 'blob',
  });
  return response.data;
}

export interface InferenceExtractionFilters {
  date_from?: string;
  date_to?: string;
  model_version?: string;
  vote?: 'correct' | 'incorrect' | 'reviewed' | 'unreviewed';
}

export async function downloadInferenceExtraction(
  filters: InferenceExtractionFilters,
): Promise<Blob> {
  const response = await apiClient.get<Blob>('/admin/inferences/extract.zip', {
    params: cleanParams(filters),
    responseType: 'blob',
  });
  return response.data;
}

function filenameFromContentDisposition(header: string | undefined, fallback: string): string {
  if (!header) return fallback;
  const match = /filename="?([^"]+)"?/.exec(header);
  return match?.[1] ?? fallback;
}

export type CreateExportResult =
  { kind: 'file'; blob: Blob; filename: string } | { kind: 'job'; job: ExportJobCreateResponse };

/**
 * POST /api/v1/exports. CSV returns a synchronous 200 file stream; xlsx/pdf/
 * docx return 202 {job_id, status} (docs/AGENT_PROMPT.md §5.1). We always
 * request as a blob so we can inspect the response Content-Type to tell
 * these two shapes apart without guessing from `format` alone.
 */
export async function createExport(request: ExportRequest): Promise<CreateExportResult> {
  const response = await apiClient.post('/exports', request, { responseType: 'blob' });
  const contentType = response.headers['content-type'] as string | undefined;

  if (contentType?.includes('application/json')) {
    const text = await (response.data as Blob).text();
    const job = JSON.parse(text) as ExportJobCreateResponse;
    return { kind: 'job', job };
  }

  const filename = filenameFromContentDisposition(
    response.headers['content-disposition'] as string | undefined,
    `paint-defect-report.${request.format}`,
  );
  return { kind: 'file', blob: response.data as Blob, filename };
}

export async function getExportStatus(jobId: string): Promise<ExportJobStatus> {
  const { data } = await apiClient.get<ExportJobStatus>(`/exports/${jobId}`);
  return data;
}

export async function downloadExport(jobId: string): Promise<{ blob: Blob; filename: string }> {
  const response = await apiClient.get(`/exports/${jobId}/download`, { responseType: 'blob' });
  const filename = filenameFromContentDisposition(
    response.headers['content-disposition'] as string | undefined,
    `paint-defect-report-${jobId}`,
  );
  return { blob: response.data as Blob, filename };
}

export interface ExportHistoryParams {
  page?: number;
  page_size?: number;
  all?: boolean;
}

export async function listExports(
  params: ExportHistoryParams,
): Promise<Paginated<ExportHistoryItem>> {
  const { data } = await apiClient.get<Paginated<ExportHistoryItem>>('/exports', {
    params: cleanParams(params),
  });
  return data;
}

/** Triggers a browser download of an in-memory Blob without a server round-trip. */
export function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}
